import { BrowserStorageUtil } from '@swift-247/s247.library.ui-core';
import config from '../../config/config';
import { envStorage } from './util';

const storage = new BrowserStorageUtil(envStorage as any);

interface PartnerThemeConfig {
  primaryColor?: string;
  secondColor?: string;
  gradientBtn?: string;
  gradientHeader?: string;
  borderRadius?: string;
  togglePadding?: string;
  toggleBg?: string;
  toggleInactiveColor?: string;
  toggleActiveBg?: string;
  toggleActiveColor?: string;
}

const hexToRgb = (hex: string): [number, number, number] | null => {
  const clean = hex.replace('#', '');
  const full = clean.length === 3
    ? clean.split('').map(c => c + c).join('')
    : clean;
  if (full.length !== 6) return null;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
};

const normalizeValue = (val: string): string =>
  val
    .trim()
    .replace(/^["']|["']$/g, '')   // strip wrapping quotes
    .replace(/\r\n|\r|\n/g, ' ')   // remove line breaks
    .replace(/\s+/g, ' ')          // collapse multiple spaces (incl. non-breaking)
    .trim();

const generatePartnerCss = (cfg: PartnerThemeConfig): string => {
  const p = cfg.primaryColor ? normalizeValue(cfg.primaryColor) : undefined;
  const second = cfg.secondColor ? normalizeValue(cfg.secondColor) : undefined;
  const btn = cfg.gradientBtn ? normalizeValue(cfg.gradientBtn) : p;
  const header = cfg.gradientHeader ? normalizeValue(cfg.gradientHeader) : undefined;
  const radius = cfg.borderRadius ? normalizeValue(cfg.borderRadius) : undefined;
  const togglePadding = cfg.togglePadding ? normalizeValue(cfg.togglePadding) : '4px';
  const toggleBg = cfg.toggleBg ? normalizeValue(cfg.toggleBg) : '#F2F2F2';
  const toggleInactiveColor = cfg.toggleInactiveColor ? normalizeValue(cfg.toggleInactiveColor) : '#888888';
  const toggleActiveBg = cfg.toggleActiveBg ? normalizeValue(cfg.toggleActiveBg) : '#FFFFFF';
  const toggleActiveColor = cfg.toggleActiveColor ? normalizeValue(cfg.toggleActiveColor) : '#1A1A1A';

  const blocks: string[] = [];

  // CSS custom properties (for components updated to use var())
  const vars: string[] = [];
  if (p) vars.push(`  --s247-primary: ${p};`);
  if (second) vars.push(`  --s247-second: ${second};`);
  if (btn) vars.push(`  --s247-gradient-btn: ${btn};`);
  if (header) vars.push(`  --s247-gradient-header: ${header};`);
  if (p) vars.push(`  --s247-icon-start: ${p};`);
  if (p) vars.push(`  --s247-icon-end: ${p};`);
  if (radius) vars.push(`  --s247-btn-radius: ${radius};`);
  vars.push(`  --s247-toggle-bg: ${toggleBg};`);
  if (vars.length) blocks.push(`:root {\n${vars.join('\n')}\n}`);

  // Direct selector overrides for compiled #b3446d in SCSS (no rebuild needed)
  if (p) {
    blocks.push(`
/* Buttons border/text */
.vjc-btn { border-color: ${p} !important; }
.vjc-btn-default { color: ${p} !important; }
.vjc-btn-link { color: ${p} !important; }
.declaration-product-summary-body-img { background-color: ${p} !important; }
.transaction-detail .transaction-detail-info .transaction-detail-info-icon-wrap { border-color: ${p} !important; }
.vjc-address-info-container.ant-card .ant-card-body .pickup-info-label span { color: ${p} !important; }
/* Radio — ant design */
.vjc-radio-button-select .ant-radio.ant-radio-checked .ant-radio-inner { border-color: ${p} !important; }
.vjc-radio-button-select .ant-radio.ant-radio-checked .ant-radio-inner::after { background-color: ${p} !important; }
.vjc-radio-button-select .ant-radio:hover .ant-radio-inner { border-color: ${p} !important; }

/* Radio — vjc-core-radio (inline SVG, override fill attr via CSS) */
.vjc-core-radio svg path { fill: ${p} !important; }
.vjc-core-radio-icon--disabled path { fill: #d2d6de !important; }

/* Checkbox */
.vjc-checkbox-group-container .ant-checkbox-checked::after,
.vjc-checkbox-container .ant-checkbox-checked::after { border-color: ${p} !important; }
.vjc-checkbox-group-container .ant-checkbox-checked .ant-checkbox-inner,
.vjc-checkbox-container .ant-checkbox-checked .ant-checkbox-inner { background-color: ${p} !important; border-color: ${p} !important; }
.vjc-checkbox-group-container .ant-checkbox:hover .ant-checkbox-inner,
.vjc-checkbox-container .ant-checkbox:hover .ant-checkbox-inner { border-color: ${p} !important; }
i-098d77dfc961d8d75
/* Chip */
.vjc-chip--active { background-color: ${p} !important; }

/* Product type switch active tab */
.product-classification-switch-icon .pc-button-item--active { background: ${btn ?? p} !important; }

/* Product classification dropdown */
.pc-dropdown-search-item:hover,
.pc-dropdown-search-item-active { color: ${p} !important; }

/* Item base (product category icons) */
.item-base:hover .item-base-container,
.item-base.item-base--active .item-base-container { background: ${p} !important; }
.item-base:hover .item-base-name,
.item-base.item-base--active .item-base-name { color: ${p} !important; }

/* Input text focus border */
.vjc-input-text-container .ant-input-affix-wrapper:hover,
.vjc-input-text-container .ant-input-affix-wrapper:focus,
.vjc-input-text-container .ant-input-affix-wrapper-focused { border-color: ${p} !important; }
.vjc-payment-card-container.ant-card .total-payment .text-total { color: ${p} !important; }
.page-title-with-tabs .ant-tabs-tab:hover { color: ${p} !important; }
/* Weight/size slider track, handle and dot */
.select-weight .ant-slider-track { background-color: ${p} !important; }
.select-weight .ant-slider-handle { background-color: ${p} !important; border-color: ${p} !important; }
.select-weight .ant-slider-handle::before { background-color: ${p} !important; }
.select-weight .ant-slider-handle::after { background-color: ${p} !important; box-shadow: 0 0 0 2px ${p} !important; }
.select-weight .ant-slider-dot { background-color: ${p} !important; border-color: ${p} !important; }
.select-weight .ant-slider-dot-active { background-color: ${p} !important; border-color: ${p} !important; }
.vjc-address-listing-item-edit-btn { color: ${p} !important; }
/* Page title active tab */
.page-title-with-tabs .ant-tabs-tab-active .ant-tabs-tab-btn { color: ${p} !important; }
.page-title-with-tabs .ant-tabs-ink-bar { background-color: ${p} !important; }
.vjc-tabs-container .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn { color: ${p} !important; }
.vjc-tabs-container .ant-tabs-ink-bar { background-color: ${p} !important; }

/* Sidebar active link */
.vjc-sidebar a.active, .vjc-sidebar .active a { color: ${p} !important; }
.vjc-menu-sidebar li a:hover, .vjc-menu-sidebar li a:focus, .vjc-menu-sidebar li a.active-link { color: ${p} !important; }

/* Header menu item list active/hover */
.vjc-menu-item-list-item-link:hover,
.vjc-menu-item-list-item-link:focus,
.vjc-menu-item-list-item-link--active { color: ${p} !important; }

/* Utility */
.text-primary { color: ${p} !important; }
.border-primary { border-color: ${p} !important; }

/* Ghost button */
.vjc-btn-container .vjc-btn-ghost,
.vjc-core-btn-secondary { background: ${toggleActiveBg} !important; border: 1px solid ${toggleBg} !important; color: ${toggleActiveColor} !important; ${radius ? `border-radius: ${radius} !important;` : ''} }

/* Address tag primary */
.vjc-address-listing-item-tag--primary { color: ${p} !important; border: 1px solid ${p} !important; }

/* Promotion modal title icon */
.ant-modal-title .promotion-title-icon { color: ${p} !important; }
    `.trim());
  }

  if (btn) {
    blocks.push(`
/* Primary button */
.vjc-core-btn-primary,
.vjc-core-btn-primary:focus,
.vjc-btn-container .vjc-btn.vjc-btn-primary,
.vjc-btn-container .vjc-btn.vjc-btn-primary:focus { background: ${btn} !important; border: none !important; color: #fff !important; }

/* Pagination active item */
.vjc-pagination-container.ant-pagination .ant-pagination-item.ant-pagination-item-active { background: ${btn} !important; color: #fff !important; margin: 0 !important; }
.vjc-pagination-container.ant-pagination .ant-pagination-item.ant-pagination-item-active a { color: #fff !important; }
    `.trim());
  }

  if (radius) {
    blocks.push(`
/* Button border radius */
.vjc-btn,
.vjc-core-btn-primary,
.vjc-btn-container .vjc-btn { border-radius: ${radius} !important; }

/* Product type switch — pill style */
.product-classification-switch-icon { background-color: ${toggleBg} !important; border-radius: ${radius} !important; padding: ${togglePadding} !important; overflow: hidden !important; width: fit-content !important; margin: 10px auto !important; }
.product-classification-switch-icon .pc-button-wrap { border-radius: ${radius} !important; border: 1px solid ${toggleBg} !important; }
.product-classification-switch-icon .pc-button-item { background: transparent !important; border: none !important; border-radius: ${radius} !important; color: ${toggleInactiveColor} !important; font-weight: 400 !important; }
.product-classification-switch-icon .pc-button-item--active { background: ${toggleActiveBg} !important; border: none !important; color: ${toggleActiveColor} !important; font-weight: 600 !important; box-shadow: 0 1px 3px rgba(0,0,0,0.12) !important; }
.product-classification-switch-icon .pc-button-item img { margin-right: 4px !important; filter: brightness(0.5) !important; }
.product-classification-switch-icon .pc-button-item--active img { margin-right: 4px !important; filter: brightness(0) !important; }
    `.trim());
  }

  if (second) {
    blocks.push(`
/* Voucher primary colors */
.promo-voucher.primary-voucher { color: ${second} !important; }
.promo-voucher.primary-voucher .promo-voucher-name { border-right-color: ${second} !important; }
.promo-voucher.primary-voucher .promo-voucher-info .promo-voucher-code { background: ${second} !important; }
.promo-voucher.primary-voucher .promo-voucher-info .text-primary { color: ${second} !important; }
.promo-voucher.primary-voucher .promo-voucher-right .promo-voucher-right-seal span { border-top-color: ${second} !important; border-bottom-color: ${second} !important; }
.promo-voucher.primary-voucher .promo-voucher-right .promo-voucher-right-seal::before { border-color: ${second} !important; }
    `.trim());
  }

  if (header) {
    blocks.push(`/* Site main header */\n.vjc-header-container .ant-layout-header { background: ${header} !important; }`);
  }

  if (p) {
    const rgb = hexToRgb(p);
    if (rgb) {
      const [r, g, b] = rgb;
      const sectionGradient = `linear-gradient(90deg, rgba(${r},${g},${b},0.1) -12.59%, rgba(${r},${g},${b},0.05) -12.57%, rgba(255,255,255,0) 134.1%)`;
      blocks.push(`/* Section card header fade */\n.vjc-section-header { background: ${sectionGradient} !important; }`);
    }
  }

  return blocks.join('\n\n');
};

const injectPartnerCss = (css: string) => {
  const id = 'partner-custom-css';
  document.getElementById(id)?.remove();
  const style = document.createElement('style');
  style.id = id;
  style.textContent = css;
  document.head.appendChild(style);
};

// Bootstrap WebView: read ?token + ?partner from URL, store in authInfo, clean URL
// Also reads #s247_theme passed by auth portal on cross-origin redirect (different ports in dev)
// Vikki App opens: /c2c-order-payment?token=xxx&partner=vikki
export const bootstrapWebView = (): boolean => {
  // Read theme + partner name injected via hash by auth portal on cross-origin redirect
  const hash = window.location.hash;
  const themeMatch = hash.match(/[#&]s247_theme=([^&]*)/);
  if (themeMatch) {
    try {
      const themeRaw = decodeURIComponent(themeMatch[1]);
      JSON.parse(themeRaw); // validate before saving
      localStorage.setItem('s247_partner_theme', themeRaw);
    } catch { /* ignore malformed */ }
  }
  const partnerMatch = hash.match(/[#&]s247_partner=([^&]*)/);
  if (partnerMatch) {
    const partnerName = decodeURIComponent(partnerMatch[1]);
    if (partnerName) localStorage.setItem('s247_webview_partner', partnerName);
  }
  if (themeMatch || partnerMatch) {
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }

  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  const partner = params.get('partner') ?? null;

  if (!token) {
    // No bypass token (e.g. partner-initiated user has no Swift247 account yet) —
    // still remember the partner so a later unauthorizedKick() carries it through
    // to the auth portal for theming + normal OTP/registration flow.
    if (partner) {
      localStorage.setItem('s247_webview_partner', partner);
      params.delete('partner');
      const newSearch = params.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '');
      window.history.replaceState(null, '', newUrl);
    }
    return false;
  }

  storage.serializedSave('authInfo', { accessToken: token, partner, themeConfig: null });

  params.delete('token');
  params.delete('partner');
  const newSearch = params.toString();
  const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '');
  window.history.replaceState(null, '', newUrl);

  return true;
};

// Fetch partner theme config from API and inject generated CSS.
// Priority: authInfo.themeConfig (token flow) → s247_partner_theme (auth portal flow) → API fetch (token flow fallback).
export const applyPartnerTheme = async (): Promise<void> => {
  const authInfo = storage.deserializedGet('authInfo');

  // 1. Cache from token-bypass flow (native app sets themeConfig directly in authInfo)
  if (authInfo?.themeConfig) {
    const css = generatePartnerCss(authInfo.themeConfig);
    if (css) injectPartnerCss(css);
    return;
  }

  // 2. Cache written by auth portal after partner login — no partner identifier needed
  const cachedRaw = localStorage.getItem('s247_partner_theme');
  if (cachedRaw) {
    try {
      const themeConfig: PartnerThemeConfig = JSON.parse(cachedRaw);
      const css = generatePartnerCss(themeConfig);
      if (css) injectPartnerCss(css);
      return;
    } catch {
      localStorage.removeItem('s247_partner_theme');
    }
  }

  // 3. Fetch from API using partner identifier (token flow only — no cached theme)
  const partner = authInfo?.partner ?? localStorage.getItem('s247_webview_partner');
  if (!partner) return;

  try {
    const res = await fetch(`${config.baseUrl}/v1/partner/${partner}/settings`);
    if (!res.ok) return;
    const data = await res.json();
    const themeConfig: PartnerThemeConfig | null = data.theme_config ?? null;
    if (themeConfig) {
      storage.serializedSave('authInfo', { ...authInfo, themeConfig });
      const css = generatePartnerCss(themeConfig);
      if (css) injectPartnerCss(css);
    }
  } catch {
    // silently fail — default theme stays
  }
};

// Returns the named partner identifier when available:
// - token flow (native app): authInfo.partner (e.g. 'vikki')
// - legacy webview flow: s247_webview_partner in localStorage
// - new auth portal flow: no named partner — returns null (use isWebView() instead)
export const getStoredPartner = (): string | null => {
  const authInfo = storage.deserializedGet('authInfo');
  return authInfo?.partner ?? localStorage.getItem('s247_webview_partner');
};

// True when running inside any partner WebView:
// - token/legacy flow: getStoredPartner() is non-null
// - new auth portal flow: s247_partner_theme cached by auth portal (no named partner)
export const isWebView = (): boolean => {
  return getStoredPartner() !== null || localStorage.getItem('s247_partner_theme') !== null;
};
